import { type FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'

import { useCreateLanguage, useLanguages } from '../api/hooks'
import type { Language } from '../api/types'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorText,
  Field,
  Input,
  Spinner,
} from '../components/ui'

export default function Dashboard() {
  const { data: languages, isPending, error } = useLanguages()
  const [creating, setCreating] = useState(false)

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">My languages</h1>
          <p className="text-sm text-muted">
            Each language is a vocabulary you invent and then learn.
          </p>
        </div>
        <Button onClick={() => setCreating((open) => !open)}>
          {creating ? 'Cancel' : 'New language'}
        </Button>
      </div>

      {creating && <NewLanguageForm onDone={() => setCreating(false)} />}

      <ErrorText error={error} />
      {isPending ? (
        <Spinner />
      ) : languages && languages.length > 0 ? (
        <ul className="grid gap-3 sm:grid-cols-2">
          {languages.map((language) => (
            <li key={language.id}>
              <LanguageCard language={language} />
            </li>
          ))}
        </ul>
      ) : (
        !creating && (
          <EmptyState
            title="No languages yet"
            hint="Create one, add a handful of words, and start practising. Or browse what other people have published."
            action={<Button onClick={() => setCreating(true)}>Create your first language</Button>}
          />
        )
      )}
    </div>
  )
}

function LanguageCard({ language }: { language: Language }) {
  return (
    <Link to={`/languages/${language.id}`} className="block h-full">
      <Card className="h-full transition hover:border-brand">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-medium text-ink">{language.name}</h2>
          {language.visibility === 'public' && <Badge tone="brand">Published</Badge>}
        </div>

        {language.description && (
          <p className="mt-1 line-clamp-2 text-sm text-muted">{language.description}</p>
        )}

        <p className="mt-3 text-sm text-muted">
          {language.entry_count} {language.entry_count === 1 ? 'word' : 'words'}
          {language.copied_from_id !== null && ' · copied'}
        </p>
      </Card>
    </Link>
  )
}

function NewLanguageForm({ onDone }: { onDone: () => void }) {
  const create = useCreateLanguage()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    create.mutate({ name, description }, { onSuccess: onDone })
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Valyrian"
            required
            maxLength={120}
            autoFocus
          />
        </Field>

        <Field label="Description (optional)">
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A harsh tongue for a cold country."
            maxLength={2000}
          />
        </Field>

        <ErrorText error={create.error} />

        <div className="flex gap-2">
          <Button type="submit" disabled={create.isPending || !name.trim()}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  )
}
